import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import Close from '@icon/Close';
import {
  setModalState,
  dissmissPracticeReminderCard
} from '@actions/practiceSession';
import { getImage } from '@utils/imageHelper';
import { Spacings, Fonts } from '@styles/index';
import {
  DPP_SET_REMINDER_CLICKED,
  DPP_SET_REMINDER_CARD_VIEWED
} from '@constants/segment';
import {
  getIsReminderSettingsActive,
  getIsReminderDisplayable,
  getIsReminderDismissible
} from '@selectors/practiceSession';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import usePracticeSegmentAnalytics from '../../usePracticeSegmentAnalytics';
import { MODAL_TYPES, QUERY_ACTIONS } from '../../constants';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  grid-column: span 18;
  height: ${Spacings.SPACING_25B};
  background-color: rgba(235, 232, 255, 1);
  box-shadow: 0px ${Spacings.SPACING_4B} ${Spacings.SPACING_8B}
    var(--color-shadow);
  margin-bottom: ${Spacings.SPACING_10B};
  border-radius: ${Spacings.SPACING_2B};

  background-image: url('${getImage('practice-session/reminder-boy.svg')}'),
    url('${getImage('practice-session/reminder-background.svg')}');
  background-position: left top, right bottom;
  background-repeat: no-repeat, no-repeat;
`;

const CloseWrapper = styled.div`
  cursor: pointer;
  position: absolute;
  top: ${Spacings.SPACING_2B};
  right: ${Spacings.SPACING_2B};
`;
const Content = styled.div`
  margin-left: 10%;
`;

const Title = styled(H5)`
  color: var(--color-i-violet-2);
  margin-bottom: ${Spacings.SPACING_1B};
`;

const Description = styled(P1)`
  color: var(--color-i-violet-2);
`;

const DummyButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 156px;
  height: 48px;
  color: var(--color-i-black);
  background: var(--color-i-white);
  border-radius: ${Spacings.SPACING_2B};
  font-size: ${Fonts.SMALL};
  font-weight: ${Fonts.BOLD};
`;

const Banner = () => {
  const dispatch = useDispatch();
  const router = useRouter();

  const { goalUID, action } = router.query;

  const isReminderSettingActive = useSelectorWithShallowEqual(
    getIsReminderSettingsActive(goalUID)
  );
  const isReminderDisplayable = useSelectorWithShallowEqual(
    getIsReminderDisplayable(goalUID)
  );
  const isReminderDismissible = useSelectorWithShallowEqual(
    getIsReminderDismissible(goalUID)
  );

  const segmentAnalytics = usePracticeSegmentAnalytics();

  const shouldHideBanner = isReminderSettingActive || !isReminderDisplayable;

  const openReminderSetting = () => {
    dispatch(
      setModalState({
        type: MODAL_TYPES.PRACTICE_REMINDER,
        data: {}
      })
    );
  };

  useEffect(() => {
    if (action === QUERY_ACTIONS.SHOW_REMINDER_SETTING) {
      openReminderSetting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  useEffect(() => {
    if (!shouldHideBanner) {
      segmentAnalytics(DPP_SET_REMINDER_CARD_VIEWED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldHideBanner]);

  const sendSetReminderEvent = () => {
    segmentAnalytics(DPP_SET_REMINDER_CLICKED);
  };

  const handleOnSetReminder = () => {
    openReminderSetting();
    sendSetReminderEvent();
  };

  const handleOnDissmiss = async () => {
    if (!isReminderDismissible) {
      return;
    }

    await dispatch(dissmissPracticeReminderCard(goalUID));
  };

  if (shouldHideBanner) {
    return null;
  }

  return (
    <>
      <Wrapper>
        {isReminderDismissible && (
          <CloseWrapper>
            <Close
              size={Spacings.SPACING_3B}
              onClick={handleOnDissmiss}
              color="var(--color-i-black)"
            />
          </CloseWrapper>
        )}
        <Content>
          <Title>Practice every day</Title>
          <Description>
            Add a reminder to complete your question goal every day
          </Description>
        </Content>
        <DummyButton onClick={handleOnSetReminder}>Set reminder</DummyButton>
      </Wrapper>
    </>
  );
};

export default Banner;
